import client from "graphql/client";
import { TRANSLATE_ITEMS } from "graphql/mutation/translation-gql";
import {
    AI_TRANSLATION_GRAPHQL_TIMEOUT_MS
} from "mock-data/translation/languages";

import {
    TRANSLATION_ERROR_TYPES,
    createTranslationError,
    isRetryableErrorType,
    normalizeTranslationError,
    validateTranslationResult
} from "./translation-ai-errors";

export const hasGraphqlTranslationAccess = () => true;

const delayReject = (ms) => {
    let timeoutId = null;
    const promise = new Promise((resolve, reject) => {
        timeoutId = setTimeout(() => {
            reject(createTranslationError(
                TRANSLATION_ERROR_TYPES.timeout,
                "GraphQL translation request timed out",
                {
                    status    : 504,
                    retryable : true
                }
            ));
        }, ms);
    });

    return {
        promise,
        cancel: () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        }
    };
};

const mutateWithTimeout = async (options) => {
    const timeoutMs = AI_TRANSLATION_GRAPHQL_TIMEOUT_MS;
    const request = client.mutate(options);

    if (!timeoutMs || timeoutMs <= 0) {
        return request;
    }

    const timeout = delayReject(timeoutMs);

    try {
        return await Promise.race([
            request,
            timeout.promise
        ]);
    } finally {
        timeout.cancel();
    }
};

const getGraphqlStatus = (error) => {
    const graphQLError = error && error.graphQLErrors && error.graphQLErrors[0];
    const extensions = graphQLError && graphQLError.extensions;
    const networkError = error && error.networkError;
    const status = (extensions && (extensions.status || extensions.statusCode)) ||
        (networkError && (networkError.statusCode || networkError.status));

    return status ? Number(status) : null;
};

const getGraphqlCode = (error) => {
    const graphQLError = error && error.graphQLErrors && error.graphQLErrors[0];
    const extensions = graphQLError && graphQLError.extensions;

    return String((extensions && (extensions.code || extensions.category)) || "").toLowerCase();
};

const getGraphqlMessage = (error) => {
    const graphQLError = error && error.graphQLErrors && error.graphQLErrors[0];
    const extensions = graphQLError && graphQLError.extensions;

    return String(
        (extensions && extensions.reason) ||
        (graphQLError && graphQLError.message) ||
        (error && error.message) ||
        ""
    );
};

const getGraphqlErrorType = (error) => {
    const status = getGraphqlStatus(error);
    const code = getGraphqlCode(error);
    const message = getGraphqlMessage(error).toLowerCase();

    if (status === 401 || status === 403) return TRANSLATION_ERROR_TYPES.authError;
    if (status === 429) return TRANSLATION_ERROR_TYPES.rateLimited;
    if (status === 404) return TRANSLATION_ERROR_TYPES.modelError;
    if (status >= 500) return TRANSLATION_ERROR_TYPES.serverError;

    if (code === "unauthenticated" || code === "forbidden" || code === "authentication") {
        return TRANSLATION_ERROR_TYPES.authError;
    }

    if (/unauthenticated|unauthorized|forbidden/.test(message)) return TRANSLATION_ERROR_TYPES.authError;
    if (/rate|quota|limit|429/.test(message)) return TRANSLATION_ERROR_TYPES.rateLimited;
    if (/model/.test(message)) return TRANSLATION_ERROR_TYPES.modelError;
    if (/timeout|timed out/.test(message)) return TRANSLATION_ERROR_TYPES.timeout;
    if (/network|failed to fetch/.test(message)) return TRANSLATION_ERROR_TYPES.network;
    if (/truncated|invalid response|reduce batch size/.test(message)) {
        return TRANSLATION_ERROR_TYPES.invalidResponse;
    }

    if (error && error.networkError) return TRANSLATION_ERROR_TYPES.network;

    return TRANSLATION_ERROR_TYPES.unknown;
};

const normalizeGraphqlError = (error) => {
    if (error && error.type) return normalizeTranslationError(error);

    const type = getGraphqlErrorType(error);
    const status = getGraphqlStatus(error);
    const message = getGraphqlMessage(error) || "GraphQL translation request failed";

    return createTranslationError(type, message, {
        status,
        retryable: isRetryableErrorType(type, status)
    });
};

const normalizeRequestItems = (items = []) => items.map((item) => ({
    key  : String(item.key),
    text : String(item.text || "")
}));

const getMutationInput = ({
        sourceLang,
        targetLang,
        namespace,
        items
    }) => ({
    lang_source : sourceLang,
    lang_target : targetLang,
    field_type  : namespace || null,
    type        : "entity",
    items       : normalizeRequestItems(items)
});

const normalizePayload = (payload) => {
    return {
        items: (payload.items || []).map((item) => ({
            key            : item.key,
            translatedText : item.text_translated
        }))
    };
};

export const translateItems = async (params) => {
    const requestItems = normalizeRequestItems(params.items);

    try {
        const response = await mutateWithTimeout({
            mutation    : TRANSLATE_ITEMS,
            variables   : {
                input: getMutationInput({
                    ...params,
                    items: requestItems
                })
            },
            fetchPolicy : "no-cache"
        });
        const payload = response && response.data && response.data.translateItems;

        return validateTranslationResult(
            normalizePayload(payload || {}),
            requestItems,
            "GraphQL translation"
        );
    } catch (error) {
        throw normalizeGraphqlError(error);
    }
};

export default translateItems;
